'use client'

import { useMutation as gqlUseMutation, useQuery } from '@apollo/client'
import { useState } from 'react'
import { toast } from 'sonner'

import { REMOVE_USER_PHOTO, UPDATE_USER_PHOTO } from '../data/mutations/profile-picture'
import { GET_CHAT_USER_TOKEN } from '../data/queries/auth'
import { GET_SIGNED_URL } from '../data/queries/user'
import { tokenUtils } from '../utils/token-utils'
import { useAuth } from './use-auth'
import { useUserQuery } from './use-user-query'

export interface UploadPictureProps {
  fileObject: Blob
  setError: (error: string) => void
  signedWorkspaceURL?: string
}

interface UpdateUserResponseDTO {
  message?: string
  status?: number
}

const uploadUserImageToS3 = async (url: string | null, file: Blob): Promise<UpdateUserResponseDTO> => {
  if (!url || !file) {
    throw new Error('URL and file are required')
  }

  try {
    const response = await fetch(url, {
      body: file,
      headers: {
        'Content-Type': file.type
      },
      method: 'PUT'
    })

    // S3 presigned URLs don't return JSON
    if (response.ok) {
      return {
        message: 'File uploaded successfully',
        status: response.status
      }
    }

    throw new Error(`Upload failed with status: ${response.status}`)
  } catch (error) {
    console.error('Error uploading to S3:', error)
    throw error
  }
}

/**
 * This is a custom hook which handles api calls for uploading and removing user photo and workspace logo
 *
 * @param props setSignedURL is setting the value of signed url of user photo received from api response
 * @param props setIsLoading is setting the api call state of image upload/remove
 * @param props setSignedWorkspaceURL is setting the value of signed url of workspace logo received from api response
 * @param props refetchDetails is a method which is fetching the user/workspace details after updating the photo
 *
 * @returns methods for updating/removing the user photo and workspace logo
 */
export const useUploadPicture = () => {
  const { signedUrl, setSignedUrl, setShowConfetti } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const { refetchUserDetails } = useUserQuery()

  // cspell:disable-next-line
  //TODO:Barath-Replace this with useLazyQuery
  const { refetch: getSignedURL } = useQuery(GET_SIGNED_URL, {
    fetchPolicy: 'no-cache',
    skip: true
  })

  const handleGetSignedURL = async (imageType: string) => {
    try {
      const data = await getSignedURL({ imageType })
      if (data.data) {
        setSignedUrl(data.data.user.url)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to get upload URL. Please try again.'
      toast.error(errorMessage)
    }
  }

  const [removeUserPhoto, { loading: removingUserPhoto }] = gqlUseMutation(REMOVE_USER_PHOTO, {
    onCompleted: () => {
      refetchUserDetails()
      toast.success('Profile picture removed successfully')
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to remove profile picture')
    }
  })

  const { refetch: getChatUserToken } = useQuery<{
    chatUserToken: {
      token: string
    }
  }>(GET_CHAT_USER_TOKEN, {
    onError: (error) => {
      toast.error(error.message || 'Failed to get chat user token')
    },
    skip: true
  })

  const [updateUserPhoto] = gqlUseMutation(UPDATE_USER_PHOTO, {
    onCompleted: async () => {
      refetchUserDetails()
      if (signedUrl) setSignedUrl('')
      toast.success('Profile picture updated successfully')

      /**
       * Get user chat token for Get stream SDK and store the token in the cookie storage
       */
      const data = await getChatUserToken()
      if (data) {
        tokenUtils.setToken('User-Chat-Token', data.data.chatUserToken.token)
      }

      // Don't automatically show confetti or redirect - let the component handle this
      setIsLoading(false)
    },
    onError: (error) => {
      setIsLoading(false)
      toast.error(error.message || 'Failed to update profile picture')
    }
  })

  interface IUploadUserPhotoParams {
    fileObject: Blob
    setError: (error: string) => void
    showConfetti?: boolean
  }

  const uploadUserPhoto = async ({ fileObject, setError, showConfetti = false }: IUploadUserPhotoParams) => {
    try {
      setIsLoading(true)
      const uploadedImageURL = signedUrl?.split('?')[0]
      const imageUploaded = await uploadUserImageToS3(signedUrl, fileObject)

      if (imageUploaded?.status === 200) {
        const input = {
          photoURL: `${uploadedImageURL}?timestamp=${Date.now()}`
        }
        await updateUserPhoto({ variables: input })

        if (showConfetti) {
          setShowConfetti(true)
        }
      } else {
        throw new Error('Upload failed')
      }
    } catch (error) {
      setIsLoading(false)
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload image'
      setError(errorMessage)
      toast.error(errorMessage)
    }
  }

  const uploadAvatarAsUserPhoto = async (avatarUrl: string, showConfetti = false) => {
    try {
      const input = {
        isPresetAvatar: true,
        photoURL: avatarUrl
      }
      setIsLoading(true)
      await updateUserPhoto({ variables: input })

      if (showConfetti) {
        setShowConfetti(true)
      }
    } catch {
      setIsLoading(false)
      toast.error('Failed to set avatar as profile picture')
    }
  }

  return {
    getSignedURL: handleGetSignedURL,
    isLoading: isLoading,
    isRemovingPhoto: removingUserPhoto,
    removeUserPhoto,
    signedUrl,
    updateUserPhoto,
    uploadAvatarAsUserPhoto,
    uploadUserPhoto
  }
}
